import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { api, fmtDate, formatErr } from "../../lib/api";
import { Empty, Field, PageHeader, StatusBadge, UploadButton, inputCls } from "../kit";
import { useSite } from "../../context/SiteContext";
import { fileUrl } from "../../lib/api";

export function Inquiries() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin-inquiries").then((r) => setItems(r.data)), []);
  useEffect(() => {
    load();
  }, [load]);
  const set = async (id, status) => {
    await api.put(`/admin-inquiries/${id}`, { status });
    load();
  };
  return (
    <div data-testid="inquiries-page">
      <PageHeader title="Inquiries" desc="Contact form submissions and support requests from the chatbot" />
      {items.length === 0 ? <Empty text="No inquiries yet." /> : (
        <div className="grid gap-3">
          {items.map((i) => (
            <div key={i.id} className="card-neon p-5" data-testid={`inquiry-${i.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold text-white">{i.name} · <a href={`mailto:${i.email}`} className="text-[#00d9ff]">{i.email}</a></p>
                <div className="flex items-center gap-2"><span className="text-xs text-slate-400">{i.inquiry_type}</span><StatusBadge s={i.status} /></div>
              </div>
              <p className="text-slate-300 whitespace-pre-wrap mt-2 text-sm">{i.message}</p>
              <div className="flex items-center justify-between mt-3"><span className="text-xs text-slate-500">{fmtDate(i.created_at)}</span>
                <button onClick={() => set(i.id, i.status === "new" ? "resolved" : "new")} className="btn-ghost !py-1.5 !px-3 text-sm" data-testid={`inquiry-toggle-${i.id}`}>{i.status === "new" ? "Mark resolved" : "Reopen"}</button></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function SettingsPage() {
  const [f, setF] = useState(null);
  const { reload } = useSite();
  useEffect(() => {
    api.get("/admin-settings").then((r) => setF(r.data));
  }, []);
  if (!f) return null;
  const save = async () => {
    try {
      setF((await api.put("/admin-settings", f)).data);
      await reload();
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <div data-testid="settings-page" className="max-w-3xl">
      <PageHeader title="Settings" desc="Branding, payments and AI assistant configuration" />
      <div className="card-neon p-6 grid gap-4">
        <h2 className="font-cond font-bold uppercase tracking-wider">Branding</h2>
        <Field label="Browser tab title"><Input value={f.site_title || ""} onChange={(e) => setF({ ...f, site_title: e.target.value })} className={inputCls} data-testid="settings-site-title" /></Field>
        <Field label="Favicon (square PNG, ICO or SVG, 64×64 or larger)">
          <div className="flex items-center gap-3">
            <img src={f.favicon ? fileUrl(f.favicon) : "/favicon.svg"} alt="Favicon preview" className="w-10 h-10 rounded border border-[#007bff]/30 bg-black/30" data-testid="settings-favicon-preview" />
            <UploadButton scope="public" accept="image/png,image/x-icon,image/svg+xml,image/jpeg,image/webp,.ico" onUploaded={(r) => setF({ ...f, favicon: r.url })} testid="settings-favicon-upload">Upload favicon</UploadButton>
            {f.favicon && <button className="text-xs text-rose-300" onClick={() => setF({ ...f, favicon: "" })} data-testid="settings-favicon-reset">Use default</button>}
          </div>
        </Field>
        <h2 className="font-cond font-bold uppercase tracking-wider pt-2">Manual payments</h2>
        <Field label="PayPal instructions shown to customers"><Textarea rows={3} value={f.paypal_instructions || ""} onChange={(e) => setF({ ...f, paypal_instructions: e.target.value })} className={inputCls} placeholder="e.g. Send to [your PayPal email] as Friends & Family, include your order number" data-testid="settings-paypal" /></Field>
        <Field label="Wise instructions shown to customers"><Textarea rows={3} value={f.wise_instructions || ""} onChange={(e) => setF({ ...f, wise_instructions: e.target.value })} className={inputCls} placeholder="e.g. [Wise account details]" data-testid="settings-wise" /></Field>
        <h2 className="font-cond font-bold uppercase tracking-wider pt-2">AI support assistant</h2>
        <label className="flex items-center gap-3 text-sm"><Switch checked={!!f.chatbot_enabled} onCheckedChange={(v) => setF({ ...f, chatbot_enabled: v })} data-testid="settings-chatbot-enabled" /> Show chatbot on public website</label>
        <Field label="Greeting message"><Input value={f.chatbot_greeting || ""} onChange={(e) => setF({ ...f, chatbot_greeting: e.target.value })} className={inputCls} data-testid="settings-greeting" /></Field>
        <Field label="Extra knowledge for the chatbot (FAQs and services are included automatically)"><Textarea rows={5} value={f.chatbot_extra_info || ""} onChange={(e) => setF({ ...f, chatbot_extra_info: e.target.value })} className={inputCls} data-testid="settings-extra-info" /></Field>
        <div className="flex gap-2"><button onClick={save} className="btn-electric !py-2" data-testid="settings-save-btn">Save</button></div>
      </div>
    </div>
  );
}
